import { CheckoutElementsProvider } from '@stripe/react-stripe-js/checkout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';

import { NoticeCard } from '@/components/feedback/notice-card';
import { OnboardingShell, StepTitle } from '@/components/onboarding/onboarding-shell';
import { WizardStepper } from '@/components/onboarding/wizard-stepper';
import { PaymentScreen } from '@/components/subscribe/payment-screen';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTheme } from '@/context';
import {
    isProvenNoOpenIntent,
    useCurrentPurchaseIntent,
    useIntentCheckoutSession,
    useWorkspaceActivated,
} from '@/hooks/use-purchase-intent';
import { useRegions } from '@/hooks/use-regions';
import { errorIds, logError } from '@/lib/log-error';
import {
    activatingHref,
    PRICING_ROUTE,
    SUBSCRIBE_STEPS,
    subscribeAccountHref,
} from '@/lib/onboarding-funnel';
import { clearPurchaseIntentHint, readPurchaseIntentHint } from '@/lib/purchase-intent-hint';
import { getStripe } from '@/lib/stripe';
import { stripeAppearance, STRIPE_FONTS } from '@/lib/stripe-appearance';

const MAX_SESSION_REPLACEMENTS = 1;

/**
 * Step 2 of the purchase funnel (ESCLOUD-332). Finds the open intent through the
 * region hint left by the account step, never by asking every region - the
 * checkout session is a query seeded there too, so an expired session is one
 * refetch away rather than a fresh POST.
 *
 * `activated` means there was nothing to pay (a €0 plan, or mock billing): the
 * workspace opens straight away.
 *
 * A completed intent found here (Back, or a stale tab reopened once the
 * webhook already ran) is sent on to activating - the hint is kept so that
 * page's region lookup still works - rather than to pricing, which would
 * ignore a workspace that already exists. Only an expired intent forgets the
 * hint and returns to pricing.
 *
 * Stripe.js failing to load is reported as such, distinct from the publishable
 * key being absent - the latter means payments are unavailable, never that the
 * plan is free.
 */
export function PaymentPage() {
    const hint = useMemo(readPurchaseIntentHint, []);
    const region = hint?.region ?? null;
    const { resolvedTheme } = useTheme();
    const regions = useRegions();
    const intent = useCurrentPurchaseIntent(region);
    const session = useIntentCheckoutSession(region, intent.data?.id ?? null, {
        enabled: intent.data?.status === 'open',
    });
    const workspaceActivated = useWorkspaceActivated();
    const stripePromise = useMemo(() => getStripe(), []);

    const [stripeScriptFailed, setStripeScriptFailed] = useState(false);
    const [replacements, setReplacements] = useState(0);
    const [sessionRefused, setSessionRefused] = useState<string | null>(null);

    useEffect(() => {
        if (!stripePromise) return;
        let cancelled = false;
        const failed = (error: unknown) => {
            if (cancelled) return;
            logError(errorIds.STRIPE_JS_LOAD_FAILED, 'Stripe.js failed to load', { error });
            setStripeScriptFailed(true);
        };
        stripePromise.then((stripe) => {
            if (!stripe) failed(new Error('Stripe.js resolved without a Stripe instance'));
        }, failed);
        return () => {
            cancelled = true;
        };
    }, [stripePromise]);

    useEffect(() => {
        if (session.data?.mode === 'activated' && region) {
            void workspaceActivated(session.data.tenantId, region);
        }
    }, [session.data, region, workspaceActivated]);

    const onSessionInvalid = useCallback(
        (message: string) => {
            if (replacements >= MAX_SESSION_REPLACEMENTS) {
                setSessionRefused(message);
                return;
            }
            setReplacements((n) => n + 1);
            void session.refetch();
        },
        [replacements, session.refetch]
    );

    // A proven "no open intent" 404 is an answer, not a failure - it redirects
    // to the account step below and must never count as the combined error
    // that triggers the "checkout could not be started" notice or its log.
    const unprovenIntentError =
        intent.error && !isProvenNoOpenIntent(intent.error) ? intent.error : undefined;
    // Logged once per distinct error rather than in the render body, which
    // would otherwise re-fire on every re-render this branch is taken on.
    const combinedError = unprovenIntentError ?? session.error ?? sessionRefused;
    useEffect(() => {
        if (!combinedError) return;
        logError(errorIds.CHECKOUT_SESSION_FAILED, 'Checkout session could not be created', {
            error: combinedError,
        });
    }, [combinedError]);

    const shell = (body: React.ReactNode, sub?: string) => (
        <OnboardingShell stepper={<WizardStepper steps={SUBSCRIBE_STEPS} currentKey="payment" />}>
            <StepTitle title="Payment" sub={sub ?? 'Check the details you chose, then enter your card.'} />
            {body}
        </OnboardingShell>
    );

    if (!hint || !region) {
        return <Navigate to={PRICING_ROUTE} replace />;
    }

    if (intent.error && isProvenNoOpenIntent(intent.error)) {
        clearPurchaseIntentHint();
        return <Navigate to={subscribeAccountHref(hint.planId)} replace />;
    }

    if (intent.data?.status === 'completed') {
        return <Navigate to={activatingHref(intent.data.id)} replace />;
    }

    if (intent.data?.status === 'expired') {
        clearPurchaseIntentHint();
        return <Navigate to={PRICING_ROUTE} replace />;
    }

    const regionName = regions.data?.items.find((r) => r.code === region)?.name ?? region.toUpperCase();
    const clientSecret = session.data?.mode === 'elements' ? session.data.clientSecret : null;

    if (stripePromise === null) {
        return shell(
            <NoticeCard
                title="Payments are not available"
                body="Online payment is not configured in this environment, so the plan cannot be activated here. Please contact contact@therenva.com."
            />
        );
    }

    if (stripeScriptFailed) {
        return shell(
            <NoticeCard
                title="The payment form could not load"
                body={
                    <>
                        A browser extension or your network is blocking our payment provider (js.stripe.com).
                        Card details are handled by the provider, never by ESCloud, so the form cannot be
                        shown without it.
                        <ol className="mt-3 mb-0 list-decimal pl-5">
                            <li>Pause your ad or tracker blocker for this page, then reload.</li>
                            <li>
                                If you are on a hospital network, try a different network or ask IT to allow
                                js.stripe.com.
                            </li>
                        </ol>
                        <span className="mt-3 block">
                            Your account details are saved. Nothing has been charged.
                        </span>
                    </>
                }
                action={
                    <div className="flex items-center gap-4">
                        <Button type="button" onClick={() => window.location.reload()}>
                            Reload the page
                        </Button>
                        <a
                            href="mailto:contact@therenva.com"
                            className="text-[13px] font-medium text-primary"
                        >
                            Contact support
                        </a>
                    </div>
                }
            />
        );
    }

    if (combinedError) {
        return shell(
            <NoticeCard
                title="Checkout could not be started"
                body="Something went wrong while contacting the payment service, and no payment has been taken. Please try again. If it keeps happening, contact contact@therenva.com."
                action={
                    <Button
                        type="button"
                        onClick={() => {
                            setSessionRefused(null);
                            setReplacements(0);
                            void intent.refetch?.();
                            void session.refetch();
                        }}
                    >
                        Try again
                    </Button>
                }
            />
        );
    }

    if (!intent.data || !clientSecret) {
        return shell(
            <div className="flex flex-col gap-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <p className="m-0 text-sm text-muted-foreground">
                    Preparing a secure payment session for the {regionName} region…
                </p>
            </div>
        );
    }

    return (
        <CheckoutElementsProvider
            key={clientSecret}
            stripe={stripePromise}
            options={{
                clientSecret,
                elementsOptions: { appearance: stripeAppearance(resolvedTheme), fonts: STRIPE_FONTS },
            }}
        >
            {/* A later theme switch is pushed into the mounted Checkout by
                PaymentScreen via `checkout.changeAppearance()`, not by
                remounting the provider on this key - remounting would
                discard whatever the buyer already typed into the card
                fields. */}
            <PaymentScreen intent={intent.data} regionName={regionName} onSessionInvalid={onSessionInvalid} />
        </CheckoutElementsProvider>
    );
}
